<script lang="ts">
  import { onMount } from 'svelte';
  import { app } from '../lib/net.svelte';
  import { SNAKE_CELLS, newSnake, step, turn, type Cell } from '../lib/snake';

  const CELL = 20;
  const STEP_MS = 110;
  const KEYS: Record<string, Cell> = {
    ArrowUp: [0, -1],
    ArrowDown: [0, 1],
    ArrowLeft: [-1, 0],
    ArrowRight: [1, 0],
    w: [0, -1],
    s: [0, 1],
    a: [-1, 0],
    d: [1, 0],
  };

  let canvas: HTMLCanvasElement | undefined = $state();
  let snake = newSnake();
  let started = $state(false);
  let over = $state(false);
  let score = $state(0);
  let best = $state(0);
  const food = new Image();
  food.src = '/favicon.png';

  function draw() {
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, SNAKE_CELLS * CELL, SNAKE_CELLS * CELL);
    if (snake.food && food.complete)
      ctx.drawImage(
        food,
        snake.food[0] * CELL,
        snake.food[1] * CELL,
        CELL,
        CELL,
      );
    snake.body.forEach(([x, y], i) => {
      ctx.fillStyle = i ? '#a8843f' : '#e6c06a';
      ctx.fillRect(x * CELL + 1, y * CELL + 1, CELL - 2, CELL - 2);
    });
  }

  function restart() {
    snake = newSnake();
    over = false;
    score = 0;
    started = true;
  }

  function onkeydown(e: KeyboardEvent) {
    const dir = KEYS[e.key.length === 1 ? e.key.toLowerCase() : e.key];
    if (over && (e.key === ' ' || e.key === 'Enter')) restart();
    else if (dir && !over) {
      turn(snake, dir);
      started = true;
    } else return;
    e.preventDefault();
  }

  onMount(() => {
    const dpr = devicePixelRatio;
    canvas!.width = canvas!.height = SNAKE_CELLS * CELL * dpr;
    canvas!.getContext('2d')!.scale(dpr, dpr);
    food.onload = draw;
    draw();
    const timer = setInterval(() => {
      if (!started || over) return;
      step(snake);
      score = snake.body.length - 3;
      best = Math.max(best, score);
      over = snake.over;
      draw();
    }, STEP_MS);
    return () => clearInterval(timer);
  });
</script>

<svelte:window {onkeydown} />

<div class="backdrop">
  <section class="panel line" aria-live="polite">
    <h2>The arena is full</h2>
    {#if app.line}
      <p class="place">
        You're <strong>#{app.line.position}</strong> of {app.line.size} in line
      </p>
    {/if}
    <p class="muted small">
      {app.status === 'offline'
        ? 'Reconnecting…'
        : "You'll go in on your own as soon as there's room. Keep this tab open."}
    </p>
  </section>

  <section class="panel snake" aria-label="Snake">
    <header>
      <span>Score {score}</span>
      <span class="muted">Best {best}</span>
    </header>
    <canvas
      bind:this={canvas}
      style:width="{SNAKE_CELLS * CELL}px"
      style:height="{SNAKE_CELLS * CELL}px"
    ></canvas>
    <p class="muted small">
      {over
        ? 'Game over. Space to play again.'
        : started
          ? 'Eat the gold.'
          : 'Arrow keys or WASD to play while you wait.'}
    </p>
  </section>
</div>

<style>
  .backdrop {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    background:
      radial-gradient(ellipse at top, #3a2f18 0%, transparent 65%), var(--bg);
  }
  .line {
    width: 360px;
    text-align: center;
    border-color: var(--gold);
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.6);
  }
  .place {
    margin: 0 0 8px;
    font-size: 18px;
  }
  .place strong {
    color: var(--gold);
  }
  .snake {
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
    background: var(--panel-solid);
  }
  .snake header {
    display: flex;
    justify-content: space-between;
    width: 100%;
    color: var(--gold);
  }
  canvas {
    background: #0b0c10;
    border: 1px solid var(--border);
  }
  .muted {
    color: var(--muted);
  }
  .small {
    font-size: 12px;
    margin: 0;
  }
</style>
